/**
 * Bundle optimization utilities for Phase 5
 */

// Dynamic import helper with error handling and retry
export const dynamicImport = async <T>(
  importFunction: () => Promise<T>,
  fallback?: () => T,
  retries = 3
): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await importFunction();
    } catch (error) {
      console.warn(`Dynamic import failed (attempt ${i + 1}/${retries}):`, error);
      
      if (i === retries - 1) {
        if (fallback) {
          console.log('Using fallback for failed dynamic import');
          return fallback();
        }
        throw error;
      }
      
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 100));
    }
  }
  
  throw new Error('All dynamic import attempts failed');
};

// Preload module for better UX
export const preloadModule = (moduleImport: () => Promise<any>) => {
  // Use requestIdleCallback to preload during idle time
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      moduleImport().catch(() => {
        // Silently fail preloading
      });
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      moduleImport().catch(() => {
        // Silently fail preloading
      });
    }, 2000);
  }
};

// Bundle analysis helpers
export const getBundleStats = () => {
  if (typeof window === 'undefined') return null;
  
  const performance = window.performance;
  if (!performance) return null;
  
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  
  const jsResources = resources.filter(r => r.name.includes('.js'));
  const cssResources = resources.filter(r => r.name.includes('.css'));
  
  return {
    navigation: {
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
      loadComplete: navigation.loadEventEnd - navigation.fetchStart
    },
    resources: {
      javascript: {
        count: jsResources.length,
        totalSize: jsResources.reduce((acc, r) => acc + (r.transferSize || 0), 0),
        avgLoadTime: jsResources.reduce((acc, r) => acc + r.duration, 0) / jsResources.length
      },
      css: {
        count: cssResources.length,
        totalSize: cssResources.reduce((acc, r) => acc + (r.transferSize || 0), 0),
        avgLoadTime: cssResources.reduce((acc, r) => acc + r.duration, 0) / cssResources.length
      }
    }
  };
};

// Chunk loading tracker
export class ChunkLoadTracker {
  private static instance: ChunkLoadTracker;
  private loadedChunks = new Set<string>();
  private loadingChunks = new Map<string, Promise<any>>();
  
  static getInstance(): ChunkLoadTracker {
    if (!ChunkLoadTracker.instance) {
      ChunkLoadTracker.instance = new ChunkLoadTracker();
    }
    return ChunkLoadTracker.instance;
  }
  
  trackChunkLoad(chunkName: string, importPromise: Promise<any>): Promise<any> {
    if (this.loadedChunks.has(chunkName)) {
      return Promise.resolve();
    }
    
    if (this.loadingChunks.has(chunkName)) {
      return this.loadingChunks.get(chunkName)!;
    }
    
    const wrappedPromise = importPromise
      .then(result => {
        this.loadedChunks.add(chunkName);
        this.loadingChunks.delete(chunkName);
        return result;
      })
      .catch(error => {
        this.loadingChunks.delete(chunkName);
        throw error;
      });
    
    this.loadingChunks.set(chunkName, wrappedPromise);
    return wrappedPromise;
  }
  
  isChunkLoaded(chunkName: string): boolean {
    return this.loadedChunks.has(chunkName);
  }
  
  getLoadingStats() {
    return {
      loaded: this.loadedChunks.size,
      loading: this.loadingChunks.size,
      loadedChunks: Array.from(this.loadedChunks)
    };
  }
}